<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        class="report-dialog"
      >
        <div class="dialog">
          <img
            @click="handleClose"
            class="close-image"
            src="@/assets/images/close-icon.svg"
            alt=""
          >
          <div class="header">
            <div class="title">
              Found a Problematic? Report It Now
            </div>
            <div class="sub-title">
              We’ll address it promptly to protect your purchasing interests
            </div>
          </div>
          <div class="body">
            <div
              v-if="reportSuccess"
              class="report-success"
            >
              <img
                src="@/assets/illus/illus-report.svg"
                alt=""
              >
              <div class="text-1">
                Report Submitted Successfully
              </div>
              <div class="text-2">
                Thank you for reporting this. We will expedite the review.
                Meanwhile, we will limit exposure/access to this [product/supplier] to prevent other customers from encountering similar problems.
              </div>
            </div>
            <template v-else>
              <div class="reason">
                <div class="title">
                  Reason for Reporting
                </div>
                <div class="reason-list">
                  <div
                    @click="activeIndex = index"
                    v-for="(item, index) in reasons"
                    :key="index"
                    :class="{ active: activeIndex === index }"
                    class="reason-list-item"
                  >
                    {{ item.desc }}
                  </div>
                </div>
              </div>
              <div class="additional">
                <div class="title">
                  With Additional Info
                </div>
                <textarea
                  v-model="additional"
                  maxlength="500"
                  placeholder="Please provide additional description to help us investigate this report. (Optional)"
                  type="text"
                />
              </div>
            </template>
          </div>
          <div class="footer">
            <div
              v-if="reportSuccess"
              @click="handleOk"
              class="button submit"
            >
              Ok
            </div>
            <template v-else>
              <div
                @click="handleClose"
                class="button cancel"
              >
                Cancel
              </div>
              <div
                @click="handleSubmit"
                class="button submit"
              >
                Submit
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue'
import { fetchData } from '@/utils/fetch'
import { buriedPointUtil } from '@/utils/utils'

const props = defineProps({
  type: {
    type: String,
    default: 'PRODUCT'
  },
  targetId: {
    type: String,
    default: ''
  }
})

const emits = defineEmits(['close'])

const reasons = ref([])

const activeIndex = ref(0)

const reportSuccess = ref(false)

const additional = ref('')

const handleOk = () => {
  emits('close')
  location.reload()
}

const handleClose = () => {
  emits('close')
  if (reportSuccess.value) {
    location.reload()
  }
}

const handleSubmit = async () => {
  const params = {
    type: props.type,
    reportUrl: location.href,
    targetId: props.targetId,
    reasonCode: reasons.value[activeIndex.value].code,
    additionalInfo: additional.value
  }
  const { code } = await fetchData('/appb/b2b/report/save', {
    method: 'post',
    body: JSON.stringify(params)
  })
  if (code === 200) {
    buriedPointUtil({
      eventId: 'PPD207',
      others: {
        type: props.type === 'PRODUCT' ? 'Products' : 'Factory',
        objectId: props.targetId
      }
    })
    reportSuccess.value = true
  }
}

const getReportReason = async () => {
  const { data } = await fetchData('/appb/b2b/report/getReportReason?type=' + props.type)
  reasons.value = data
}

onMounted(() => {
  getReportReason()
  document.documentElement.style.overflow = 'hidden'
})

onBeforeUnmount(() => {
  document.documentElement.style.overflow = ''
})

</script>

<style lang='less' scoped>
.report-dialog {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, .64);
  backdrop-filter: blur(8px);
  z-index: 999;

  .dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    width: 720px;
    height: 584px;
    .close-image {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 28px;
      height: 28px;
      cursor: pointer;
    }
    .header {
      margin: 24px 32px 0;
      .title {
        color: #232323;
        font-size: 20px;
        font-family: mon-700;
        line-height: 28px;
      }
      .sub-title {
        margin-top: 3px;
        color: #232323;
        font-size: 12px;
        font-family: mon-500;
      }
    }
    .body {
      margin: 32px;

      .report-success {
        margin-top: 32px;
        .text-1 {
          margin-top: 8px;
          line-height: 24px;
          color: #232323;
          font-size: 16px;
          font-family: mon-700;
        }
        .text-2 {
          color: #232323;
          font-size: 12px;
          font-family: mon-500;
          width: 480px;
          line-height: 18px;
          margin-top: 3px;
        }
      }

      .reason {
        .title {
          color: #232323;
          font-size: 16px;
          font-family: mon-700;
        }
        .reason-list {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-top: 12px;
          .reason-list-item {
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            width: 208px;
            height: 64px;
            padding: 16px;
            border: 1px solid #ebf1f5;
            border-radius: 8px;
            color: #232323;
            font-size: 12px;
            font-family: mon-600;

            &:hover {
              border: 2px solid #232323;
              cursor: pointer;
            }

            &.active {
              border: 2px solid #232323;
            }
          }
        }
      }

      .additional {
        margin-top: 24px;
        .title {
          color: #232323;
          font-size: 16px;
          font-family: mon-700;
        }

        textarea {
          outline: none;
          resize: none;
          margin-top: 12px;
          border: 1px solid #ebf1f5;
          border-radius: 8px;
          height: 120px;
          width: 100%;
          padding: 16px;
          &::placeholder {
            color: #b2b7c2;
            font-size: 11px;
            font-family: mon-500;
          }
          &:focus {
            border: 1px solid #232323;
          }
        }
      }
    }
    .footer {
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 88px;
      border-top: 1px solid #ebf1f5;
      padding: 0 24px;
      display: flex;
      justify-content: end;
      .button {
        width: 180px;
        height: 44px;
        line-height: 44px;
        text-align: center;
        font-size: 14px;
        font-family: mon-600;
        border-radius: 22px;
        margin-top: 20px;
        cursor: pointer;
        &.cancel {
          background-color: #f8f8f8;
          color: #232323;
        }
        &.submit {
          background-color: #052784;
          color: white;
          margin-left: 16px;
        }
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
